/**
 * v1 `surveillance_traffic/<emitter>` adapter.
 *
 * Each per-emitter payload looks like:
 *   { now: <epoch ms>, emitter: 'live_ads' | 'live_vision' | ...,
 *     emitter_device_id?: string,
 *     aircraft: [{ hex, flight, alt_baro, alt_geom, gs, track?, nav_heading?,
 *                  squawk, category, lat, lon, ... }, ...] }
 *
 * Output: an array of partial v2 `Target` records (without
 * proximity-against-drone fields). The MqttSourceLegacy fuses emitters
 * across messages and emits a single `targets` envelope on a cadence.
 *
 * Trust mapping (matches v1 aircraftStore SOURCE_PRIORITY intent, post
 * ticket 86e1fvf06):
 *   live_ads / live_mlat / live_vision   → high
 *   live_drone_rf                        → medium (RF-detected, less reliable)
 *   live_regional_*                      → medium
 *
 * Excluded emitters (live_community / live_ads_count / metrics / stream)
 * are filtered upstream by the ALLOWED_EMITTERS_SET allowlist; we never
 * reach `trustFromEmitter` for them.
 *
 * Category → type:
 *   A5 (Heavy)          → 'heavy'
 *   A3 / A4 (Large/HV)  → 'medium'
 *   A1 / A2 (Light/SmL) → 'light'
 *   default             → 'medium'
 */
import { z } from 'zod';
import type { Target } from '@/lib/mqtt/schemas/targets';

export const V1SurveillanceTrafficPayload = z.object({
  emitter: z.string(),
  emitter_device_id: z.string().nullish(),
  now: z.number().nullish(),                 // epoch ms
  ctime: z.number().nullish(),               // legacy alternative
  aircraft: z.array(z.unknown()),
});
export type V1SurveillanceTrafficPayload = z.infer<typeof V1SurveillanceTrafficPayload>;

// Loose schema for a single aircraft row — v1 publishers vary in fields
// (live_ads has `track`, live_vision uses `nav_heading`, etc.). We accept
// what's there and synthesise the rest.
function nonBlank(v: string | null | undefined): string | null {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length > 0 ? s : null;
}

const V1Aircraft = z.object({
  hex: z.union([z.string(), z.number()]).nullish(),
  flight: z.string().nullish(),
  t: z.string().nullish(),                    // type code (community feeds)
  r: z.string().nullish(),                    // registration (community feeds)
  desc: z.string().nullish(),                 // make + model text (tar1090 `desc`)
  alt_baro: z.union([z.number(), z.string()]).nullish(),
  alt_geom: z.number().nullish(),
  gs: z.number().nullish(),
  track: z.number().nullish(),
  nav_heading: z.number().nullish(),
  squawk: z.union([z.number(), z.string()]).nullish(),
  category: z.string().nullish(),
  lat: z.number().nullish(),
  lon: z.number().nullish(),
});
type V1Aircraft = z.infer<typeof V1Aircraft>;

const EMITTER_TRUST: Record<string, 'high' | 'medium' | 'low'> = {
  live_ads: 'high',
  live_mlat: 'high',
  live_vision: 'high',
  live_drone_rf: 'medium',
};
export function trustFromEmitter(emitter: string): 'high' | 'medium' | 'low' {
  if (EMITTER_TRUST[emitter]) return EMITTER_TRUST[emitter]!;
  if (emitter.startsWith('live_regional_')) return 'medium';
  return 'medium';
}

function targetTypeFromCategory(cat: string | null | undefined): 'heavy' | 'medium' | 'light' {
  if (!cat) return 'medium';
  const c = cat.toUpperCase();
  if (c === 'A5') return 'heavy';
  if (c === 'A3' || c === 'A4') return 'medium';
  if (c === 'A1' || c === 'A2') return 'light';
  return 'medium';
}

/** v1 `gs` is groundspeed in knots already for `live_ads`/`live_community`,
 *  but `live_vision` sometimes publishes m/s. We have no flag — guess: gs
 *  values above 600 are almost certainly knots (commercial), below ~60
 *  could be m/s for low-speed targets. Conservative: pass through as-is
 *  and accept the noise — the UI tolerates fuzziness. */
function knotsFromGs(gs: number | null | undefined): number {
  return gs ?? 0;
}

/** Pick the more reliable altitude. `alt_geom` is GPS-derived (WGS-84);
 *  `alt_baro` is barometric. Targets envelope wants feet. v1 publishes
 *  both in feet already (verified from real payloads). When `alt_baro`
 *  is "ground" (string), use 0. */
function altitudeFt(a: V1Aircraft): number {
  if (typeof a.alt_geom === 'number') return Math.round(a.alt_geom);
  if (typeof a.alt_baro === 'number') return Math.round(a.alt_baro);
  if (a.alt_baro === 'ground') return 0;
  return 0;
}

/** Stable key for fusion across emitters. v1 aircraftStore prefers
 *  flight (callsign) over hex because vision-only contacts have hex="0"
 *  but a unique flight name. Position-based fallback is last-resort. */
export function aircraftKey(a: V1Aircraft): string {
  const flight = (a.flight ?? '').trim();
  if (flight && flight !== '0') return flight;
  const hex = typeof a.hex === 'string' ? a.hex.trim() : a.hex != null ? String(a.hex) : '';
  if (hex && hex !== '0') return hex;
  return `pos_${a.lat ?? 0}_${a.lon ?? 0}`;
}

export interface PartialTarget extends Omit<Target, 'dist_km' | 'vert_sep_ft' | 'horiz_sep_nm' | 'proximity'> {
  // Proximity calculations live in the source (need selected-drone state).
}

/**
 * Convert one parsed surveillance_traffic payload into an array of
 * partial Target records. Returns null if the payload is malformed.
 *
 * `topicEmitter` (= topic suffix, e.g. `live_regional_edmonton`) is the
 * canonical id and OVERRIDES the payload's `emitter` field, which for
 * regionals is abbreviated (`live_regional_edm` vs `_edmonton`). Using
 * the topic suffix keeps `source_services` tags consistent with the
 * service ids used everywhere else (pill row, store keys, etc.).
 */
export function surveillanceTrafficToPartialTargets(
  topicEmitter: string,
  raw: unknown,
  receivedAtIso: string,
): { emitter: string; targets: PartialTarget[] } | null {
  const parsed = V1SurveillanceTrafficPayload.safeParse(raw);
  if (!parsed.success) return null;
  const env = parsed.data;
  const trust = trustFromEmitter(topicEmitter);

  const targets: PartialTarget[] = [];
  for (const item of env.aircraft) {
    const acParse = V1Aircraft.safeParse(item);
    if (!acParse.success) continue;
    const a = acParse.data;
    if (a.lat == null || a.lon == null) continue;
    if (a.lat < -90 || a.lat > 90 || a.lon < -180 || a.lon > 180) continue;

    const key = aircraftKey(a);
    const flight = (a.flight ?? '').trim();
    const hex =
      typeof a.hex === 'string' ? a.hex.trim() : a.hex != null ? String(a.hex).trim() : '';

    targets.push({
      target_id: key,
      callsign: flight || hex || key,
      icao_hex: hex && hex !== '0' ? hex : null,
      lat: a.lat,
      lng: a.lon,
      heading: ((a.track ?? a.nav_heading ?? 0) + 360) % 360,
      altitude_ft: altitudeFt(a),
      speed_kts: knotsFromGs(a.gs),
      type: targetTypeFromCategory(a.category),
      trust,
      source_services: [topicEmitter],
      received_at: receivedAtIso,
      // Raw ADSB category (A1..A5 etc.) and ICAO aircraft-type
      // designator (`t` field) — passed through so the tar1090 marker
      // adapter can pick the right silhouette. v1's `live_ads` payload
      // omits `t`; regionals + community include it.
      category: typeof a.category === 'string' && a.category.length > 0 ? a.category : null,
      aircraft_type: typeof a.t === 'string' && a.t.length > 0 ? a.t : null,
      registration: nonBlank(a.r),
      description: nonBlank(a.desc),
      rf: null,
    });
  }
  return { emitter: topicEmitter, targets };
}
